import { useState, useEffect, useCallback, useRef } from 'react';
import type { CrossTabMessage } from 'src/services/Session';

/**
 * useCrossTabSessionSync Hook
 * 
 * Synchronizes session state across browser tabs using BroadcastChannel API.
 * When user is active in one tab, all tabs are kept alive. When user logs out
 * in one tab, all tabs are logged out.
 * 
 * Features:
 * - Activity synchronization across tabs
 * - Warning modal synchronization
 * - Session extension synchronization
 * - Logout synchronization
 * - Automatic cleanup on unmount
 */

interface CrossTabSessionState {
  /** Global last activity timestamp across all tabs */
  globalLastActivity: number;
  /** Whether warning is shown in any tab */
  isWarningShownGlobally: boolean;
  /** Whether session was extended in another tab */
  wasExtendedInOtherTab: boolean;
}

interface UseCrossTabSessionSyncOptions {
  /** Channel name for BroadcastChannel (default: 'admin-session') */
  channelName?: string;
  /** Enable cross-tab sync (default: true) */
  enabled?: boolean;
  /** Callback when activity detected in another tab */
  onActivityInOtherTab?: (timestamp: number) => void;
  /** Callback when warning shown in another tab */
  onWarningInOtherTab?: () => void;
  /** Callback when session extended in another tab */
  onExtendedInOtherTab?: () => void;
  /** Callback when logout triggered in another tab */
  onLogoutInOtherTab?: () => void;
}

export const useCrossTabSessionSync = (
  options: UseCrossTabSessionSyncOptions = {}
): {
  sessionState: CrossTabSessionState;
  broadcastActivity: (timestamp?: number) => void;
  broadcastWarning: () => void;
  broadcastExtended: () => void;
  broadcastLogout: () => void;
} => {
  const {
    channelName = 'admin-session',
    enabled = true,
    onActivityInOtherTab,
    onWarningInOtherTab,
    onExtendedInOtherTab,
    onLogoutInOtherTab,
  } = options;

  const [sessionState, setSessionState] = useState<CrossTabSessionState>({
    globalLastActivity: Date.now(),
    isWarningShownGlobally: false,
    wasExtendedInOtherTab: false,
  });

  const channelRef = useRef<BroadcastChannel | null>(null);

  // Initialize BroadcastChannel
  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      channelRef.current = new BroadcastChannel(channelName);

      // Listen for messages from other tabs
      channelRef.current.onmessage = (event: MessageEvent<CrossTabMessage>) => {
        const message = event?.data;

        // Validate message exists and has required properties
        if (!message || !message.type) {
          console.warn('[useCrossTabSessionSync] Received invalid message:', message);
          return;
        }

        switch (message.type) {
          case 'activity': {
            const activityTime = message.timestamp || Date.now();
            setSessionState((prev: CrossTabSessionState) => ({
              ...prev,
              globalLastActivity: activityTime,
              isWarningShownGlobally: false,
              wasExtendedInOtherTab: false,
            }));
            onActivityInOtherTab?.(activityTime);
            break;
          }

          case 'warning': {
            setSessionState((prev: CrossTabSessionState) => ({
              ...prev,
              isWarningShownGlobally: true,
            }));
            onWarningInOtherTab?.();
            break;
          }

          case 'extended': {
            const extendedTime = message.timestamp || Date.now();
            setSessionState((prev: CrossTabSessionState) => ({
              ...prev,
              globalLastActivity: extendedTime,
              isWarningShownGlobally: false,
              wasExtendedInOtherTab: true,
            }));
            onExtendedInOtherTab?.();
            break;
          }

          case 'logout': {
            onLogoutInOtherTab?.();
            break;
          }
        }
      };

      // Cleanup on unmount
      return () => {
        channelRef.current?.close();
        channelRef.current = null;
      };
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to initialize BroadcastChannel:', error);
    }
  }, [enabled, channelName, onActivityInOtherTab, onWarningInOtherTab, onExtendedInOtherTab, onLogoutInOtherTab]);

  // Broadcast activity to other tabs
  const broadcastActivity = useCallback((timestamp: number = Date.now()) => {
    if (!enabled || !channelRef.current) return;

    try {
      const message: CrossTabMessage = {
        type: 'activity',
        timestamp,
      };
      channelRef.current.postMessage(message);
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to broadcast activity:', error);
    }
  }, [enabled]);

  // Broadcast warning to other tabs
  const broadcastWarning = useCallback(() => {
    if (!enabled || !channelRef.current) return;

    try {
      const message: CrossTabMessage = {
        type: 'warning',
        timestamp: Date.now(),
      };
      channelRef.current.postMessage(message);
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to broadcast warning:', error);
    }
  }, [enabled]);

  // Broadcast session extension to other tabs
  const broadcastExtended = useCallback(() => {
    if (!enabled || !channelRef.current) return;

    try {
      const message: CrossTabMessage = {
        type: 'extended',
        timestamp: Date.now(),
      };
      channelRef.current.postMessage(message);
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to broadcast extension:', error);
    }
  }, [enabled]);

  // Broadcast logout to other tabs
  const broadcastLogout = useCallback(() => {
    if (!enabled || !channelRef.current) return;

    try {
      const message: CrossTabMessage = {
        type: 'logout',
        timestamp: Date.now(),
      };
      channelRef.current.postMessage(message);
    } catch (error) {
      console.error('[useCrossTabSessionSync] Failed to broadcast logout:', error);
    }
  }, [enabled]);

  return {
    sessionState,
    broadcastActivity,
    broadcastWarning,
    broadcastExtended,
    broadcastLogout,
  };
};
